import { useReduxState } from 'hooks';
import { getLikertOptionColors } from 'lib/blocks';
import { FormGroup } from 'components/common/form';
import { EditorBlockTranslationReference } from 'components/editor';
import { EditorBlockLikertOption, EditorBlockLikertOptionsConfig } from '.';

import type { BlockLikert, Course } from 'shared-types';
import type { BlockLikertStateProps } from './EditorBlockLikert';

interface Props {
  blockId: number;
  data: BlockLikert;
  isPreview?: boolean;
  languageVersion: Course['language'];
  updateData: (data: BlockLikert) => void;
}

const EditorBlockLikertOptions = ({
  blockId,
  data,
  isPreview = false,
  languageVersion,
  updateData,
  ...stateProps
}: Props & BlockLikertStateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { theme } = useReduxState();

  const handleChangeColormap = (colormap: BlockLikert['colormap']) => {
    updateData({ ...data, colormap });
  };

  const handleOptionChange = (index: number, value: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [`option_${index + 1}`]: value
          }
        }
      }
    });
  };

  const handleNumberOfOptionsChange = (value: number) => {
    const newOptionsArray = Array.from(
      { length: value },
      (_, i) => `{{ locales.translatable.option_${i + 1} }}` as const
    );
    const currentTranslatable =
      data?.locales[languageVersion]?.translatable || {};
    const newTranslatableOptions = {};
    for (let i = 1; i <= value; i++) {
      newTranslatableOptions[`option_${i}`] =
        currentTranslatable[`option_${i}`] || '';
    }

    updateData({
      ...data,
      options: newOptionsArray,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            ...newTranslatableOptions
          }
        }
      }
    });
  };

  const likertColors = getLikertOptionColors({
    colormapType: data.colormap,
    numberOfColors: data.options.length,
    theme
  });

  return (
    <FormGroup
      maxWidth="100%"
      style={{ marginTop: 25 }}
      label={
        <EditorBlockLikertOptionsConfig
          blockId={blockId}
          currentColormap={data.colormap || 'divergent'}
          currentNumberOfOptions={data.options.length || 5}
          isPreview={Boolean(editorLanguageActive) || isPreview}
          onColormapSelection={handleChangeColormap}
          onOptionsSelection={handleNumberOfOptionsChange}
        />
      }
    >
      {data.options.map((_, index) => {
        const referenceValue =
          data.locales[editorLanguageReference]?.translatable?.[
            `option_${index + 1}`
          ] || '';
        const optionValue =
          data.locales[languageVersion]?.translatable?.[
            `option_${index + 1}`
          ] || '';
        return (
          <div key={`${blockId}-likert-${index}`} style={{ marginBottom: 12 }}>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isEnabled={Boolean(editorLanguageActive)}
              isInnerElement={true}
              referenceHtml={referenceValue}
              style={{ marginTop: 5, marginBottom: 5 }}
              translationHtml={optionValue}
            >
              <EditorBlockLikertOption
                color={likertColors?.[index]?.backgroundColor}
                index={index}
                isPreview={isPreview}
                onChange={handleOptionChange}
                value={optionValue}
              />
            </EditorBlockTranslationReference>
          </div>
        );
      })}
    </FormGroup>
  );
};

export default EditorBlockLikertOptions;
